import { useState, useEffect } from 'react';

import { InfoIcon, ShieldBan } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { useUpdateAdminApprovalLimit } from '@/api/mutations/useUpdateAdminApprovalLimit';
import { useGetUsersOrganisation } from '@/api/queries/useGetUsersOrganisation';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Button } from '@/components/atoms/new-button';
import { useHasPermission } from '@/hooks/useHasPermission';

export const AdminApprovalLimit = () => {
  const { data: organisation } = useGetUsersOrganisation();
  const updateAdminApprovalLimitMutation = useUpdateAdminApprovalLimit();

  const [higherApprovalLimit, setHigherApprovalLimit] = useState<string>('');
  const [isAdminApprovalLimitLoading, setIsAdminApprovalLimitLoading] = useState(false);
  const [hasUnsavedReviewerChanges, setHasUnsavedReviewerChanges] = useState(false);

  const { hasPermission } = useHasPermission();

  // Initialize values when organisation data is loaded
  useEffect(() => {
    if (
      organisation?.adminApprovalTokenLimit !== undefined &&
      organisation.adminApprovalTokenLimit !== null
    ) {
      setHigherApprovalLimit(organisation.adminApprovalTokenLimit.toString());
    }
  }, [organisation]);

  const handleHigherApprovalLimitChange = (value: string) => {
    setHigherApprovalLimit(value);
    const originalValue = organisation?.adminApprovalTokenLimit?.toString() || '';
    setHasUnsavedReviewerChanges(value !== originalValue);
  };

  const handleSaveAdminApprovalLimit = async () => {
    setIsAdminApprovalLimitLoading(true);
    try {
      const adminApprovalTokenLimit = higherApprovalLimit ? parseInt(higherApprovalLimit, 10) : undefined;

      if (!adminApprovalTokenLimit) {
        toast.error('Please enter a valid approval limit');
        return;
      }

      await updateAdminApprovalLimitMutation.mutateAsync({
        adminApprovalTokenLimit,
      });

      toast.success('Approval limit updated successfully');
      setHasUnsavedReviewerChanges(false);
    } catch (error) {
      toast.error('Failed to save approval limit');
    } finally {
      setIsAdminApprovalLimitLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
          <ShieldBan className="text-purple-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Admin Approval Limit</h2>
          <p className="text-sm text-gray-600">Configure limit for uploads requiring additional approval.</p>
        </div>
      </div>

      {hasPermission(Permission.TokenLimitsManage) ? (
        <>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="higher-approval-limit" className="text-sm font-medium text-gray-700">
                Admin Approval Token Limit (Optional)
              </Label>
              <Input
                id="higher-approval-limit"
                type="number"
                value={higherApprovalLimit}
                onChange={(e) => handleHigherApprovalLimitChange(e.target.value)}
                placeholder="Enter admin approval limit"
                min="0"
                step="1000"
              />
              <p className="text-xs text-gray-500">
                Uploads exceeding this limit will require approval from designated reviewers. Leave empty to disable
                admin approval.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Designated Reviewers</Label>

              <p className="text-xs text-gray-500">
                In order to make someone a reviewer, you need to add the <b>runs:approve</b> permission to their role.
                Administrators by default have this permission, but if you want to create a new role for reviewers, you
                can do so in the{' '}
                <NavLink to="/app/chronos/configuration/roles" className="text-blue-500">
                  Roles
                </NavLink>{' '}
                section.
              </p>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
            <div className="text-sm text-gray-500">
              {hasUnsavedReviewerChanges ? (
                <span className="inline-flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                  You have unsaved changes
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  All changes saved
                </span>
              )}
            </div>

            <Button
              variant="brand"
              onClick={handleSaveAdminApprovalLimit}
              disabled={isAdminApprovalLimitLoading || !hasUnsavedReviewerChanges}
              size="sm"
            >
              {isAdminApprovalLimitLoading ? 'Saving...' : 'Save Limit'}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm">
          <InfoIcon className="h-4 w-4 text-gray-500" />
          <p className="text-sm text-gray-500">
            You do not have permission to manage reviewer configuration. Please contact your administrator or the Wexler
            team.
          </p>
        </div>
      )}
    </div>
  );
};
